/* VB · JS base (sin dependencias, cargado con defer).
   API pública en window.VB:
   - VB.cart.add(items, {sections})   items: [{id, quantity, properties}]
   - VB.cart.refresh()                 re-renderiza el carrito lateral y el contador
   - VB.cart.discount(add, remove, {ifEmpty})  aplica/quita códigos de descuento del carrito
   - VB.cart.open() / close()
   - VB.formatMoney(cents)
   - VB.scroller(root)                 flechas/puntos para [data-vb-scroller]
   Eventos en document: 'vb:cart:updated' (detail: cart)
*/
(() => {
  const P = (window.VB = window.VB || {});
  const routes = () => P.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change' };

  /* Dinero ------------------------------------------------------------ */
  P.formatMoney = (cents, format = P.money || '{{amount_with_comma_separator}} €') => {
    const n = Number(cents || 0) / 100;
    const fmt = (dec, ts, ds) => {
      const [i, d] = n.toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, ts) + (d ? ds + d : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: fmt(2, ',', '.'),
      amount_no_decimals: fmt(0, ',', '.'),
      amount_with_comma_separator: fmt(2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(0, '.', ','),
      amount_with_apostrophe_separator: fmt(2, "'", '.'),
    }[k] ?? fmt(2, '.', ',')));
  };

  /* Carrito ------------------------------------------------------------- */
  const drawerSections = () => [...document.querySelectorAll('[data-vb-cart-section]')].map((el) => el.dataset.vbCartSection);
  const applySections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      document.querySelectorAll(`[data-vb-cart-section="${id}"]`).forEach((target) => {
        const src = doc.querySelector(`[data-vb-cart-section="${id}"]`);
        if (src) target.innerHTML = src.innerHTML;
      });
    });
  };
  const setCount = (count) => document.querySelectorAll('[data-vb-cart-count]').forEach((el) => {
    el.textContent = count;
    el.hidden = !count && !el.closest('[data-zero-count="true"]'); // Figma: el globo se ve también a 0
  });

  /* Respuesta del carrito: si el servidor no devuelve JSON (error 5xx, página de bloqueo) no se enseña el error técnico */
  const CART_ERR = { es: 'No se ha podido actualizar el carrito. Inténtalo de nuevo.', en: 'We couldn\'t update your cart. Please try again.', fr: 'Impossible de mettre à jour le panier. Réessaie.', de: 'Der Warenkorb konnte nicht aktualisiert werden. Bitte versuche es erneut.', it: 'Non è stato possibile aggiornare il carrello. Riprova.', pt: 'Não foi possível atualizar o carrinho. Tenta novamente.' };
  const cartError = () => (window.VB && VB.cartError) || CART_ERR[(document.documentElement.lang || 'es').slice(0, 2)] || CART_ERR.es;
  async function readJson(res) {
    try { return await res.json(); } catch (e) { throw new Error(cartError()); }
  }

  P.cart = {
    async add(items, opts = {}) {
      const sections = [...new Set([...(opts.sections || []), ...drawerSections()])];
      const res = await fetch(`${routes().cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.description || data.message || cartError());
      // Antes de pintar: si el añadido cambia el código de pack, se aplica primero y se pinta ya con descuento
      // (antes el cajón salía a precio completo y 300 ms después se repintaba con el descuento — QA Pablo 28-oct)
      let cart = null;
      let synced = null;
      try { cart = await fetch(`${routes().cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()); } catch (e) { /* 429 tras un add correcto: las líneas ya están */ }
      if (cart && Array.isArray(cart.items)) { try { synced = await P.cart.syncPacks(cart); } catch (e) { synced = null; } }
      if (synced && synced !== cart && synced.sections) {
        // discount() ya pintó las secciones con el código nuevo y avisó con vb:cart:updated
      } else {
        applySections(data.sections);
        if (cart && Array.isArray(cart.items)) {
          setCount(cart.item_count);
          document.dispatchEvent(new CustomEvent('vb:cart:updated', { detail: cart }));
        }
      }
      if (P.cartType === 'page') location.href = routes().cart;
      else if (opts.open !== false && !document.querySelector('.vb-mc')) P.cart.open();   // open:false → no abre el cajón (pack de móvil)
      return data;
    },
    async change(line, quantity) {
      const sections = drawerSections();
      const res = await fetch(`${routes().cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections, sections_url: location.pathname }),
      });
      const cart = await readJson(res);
      // 422 = sin más stock: el servidor no cambia nada y no devuelve secciones; se avisa en la línea
      if (!res.ok) throw new Error(cart.description || cart.message || cartError());
      // Igual que add(): si el cambio cruza un nivel de pack, el código se aplica antes de pintar (sin parpadeo)
      let synced = null;
      try { synced = await P.cart.syncPacks(cart); } catch (e) { synced = null; }
      // syncPacks devuelve el MISMO carrito si no cambia ningún código (y ese ya trae .sections de change.js):
      // solo se da por pintado si discount() devolvió un carrito nuevo
      if (synced && synced !== cart && synced.sections) return synced;
      applySections(cart.sections);
      setCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('vb:cart:updated', { detail: cart }));
      return cart;
    },
    /* Descuentos de pack por CÓDIGOS (VB.packSync, layout/theme.liquid ← Ajustes del tema › Tarjeta de producto).
       Calcula qué códigos corresponden al carrito y deja exactamente esos (conserva los códigos ajenos):
       · Crea tu pack: líneas con la misma _pack = un pack; nivel por unidades (2/3/4 → mixCodes[0/1/2]); vale el mayor.
       · Packs del mismo producto (líneas sin _pack, cuentan también las de _upsell): tramo más alto alcanzado según
         «% por unidades» (o el % del producto) → prefijo + unidades + primera palabra del handle (PACK2LIPSMOOTH).
       · Combínalo: _upsell=all en ≥ 2 productos → upsellCodes[1]; si no, _upsell=item con ≥ 2 productos → upsellCodes[0].
       Activo solo en un tema sin publicar o con «Descuentos activos»; con la app (packByApp) solo retira los suyos.
       Lo llaman el constructor de packs, la ficha y el listener global de abajo (vb:cart:updated). */
    async syncPacks(cart) {
      const C = P.packSync || {};
      if (!cart || !Array.isArray(cart.items)) return cart;
      const up = (c) => String(c || '').trim().toUpperCase();
      const list = (v) => String(v || '').split(',').map((c) => c.trim()).filter(Boolean);
      // «2:17» (unidades:%) o «2=34,99» (unidades=total fijo): para elegir el código solo cuentan las unidades
      const pairs = (v) => String(v || '').split(';').map((x) => (x.includes('=') ? [parseInt(x, 10), 1] : x.split(':').map((n) => parseInt(String(n).replace('%', ''), 10))))
        .filter(([q, d]) => q > 0 && d > 0 && d < 100).sort((a, b) => a[0] - b[0]);
      const mixT = pairs(C.mix);
      const mixCodes = list(C.mixCodes);
      const upCodes = list(C.upsellCodes);
      const prefix = up(C.prefix);
      // Códigos «nuestros» = los de los ajustes + los de pack que este navegador aplicó (los de la clienta no se tocan)
      const KEY = 'vb:pk-codes';
      let applied = [];
      try { applied = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { applied = []; }
      const own = new Set([...mixCodes, ...upCodes, ...(P.packCodes || []), ...(Array.isArray(applied) ? applied : [])].map(up));
      const isOwn = (c) => own.has(up(c)) || upCodes.some((b) => b && up(c).startsWith(up(b)) && /^[A-Z]+$/.test(up(c).slice(up(b).length)));
      const role = window.Shopify && Shopify.theme && Shopify.theme.role;
      const active = !P.packByApp && (C.live === true || (!!role && role !== 'main'));
      const want = [];
      if (active) {
        const pk = (it) => (it.properties && it.properties._pack) || '';
        const tag = (it) => (it.properties && it.properties._upsell) || '';
        const groups = {};
        cart.items.forEach((it) => { if (pk(it)) groups[pk(it)] = (groups[pk(it)] || 0) + it.quantity; });
        let mi = -1;
        Object.values(groups).forEach((n) => mixT.forEach(([q], i) => { if (n >= q && i > mi) mi = i; }));
        const mixCode = mi >= 0 ? mixCodes[mi] : '';
        if (prefix) {
          const units = {};
          cart.items.forEach((it) => { if (!pk(it)) units[it.handle] = (units[it.handle] || 0) + it.quantity; });
          const rows = String(C.byProduct || '').split(/\r?\n/);
          Object.keys(units).forEach((h) => {
            const row = rows.find((l) => l.includes('|') && l.split('|')[0].trim() === h);
            const tiers = pairs(row !== undefined ? row.slice(row.indexOf('|') + 1) : C.tiers);
            let q = 0;
            tiers.forEach(([k]) => { if (units[h] >= k) q = k; });
            if (q) want.push(`${prefix}${q}${up(String(h).split('-')[0]).replace(/[^A-Z0-9]/g, '')}`);
          });
        }
        // Crea tu pack (VBPACK5/10/15) descuenta TODO el pedido: con packs sueltos ya rebajados (PACK…) no se aplica,
        // para no sumar dos descuentos sobre las mismas unidades (Nicolas 31-oct)
        if (mixCode && !want.length) want.push(mixCode);
        // Combínalo: un código POR PRODUCTO (base + primera palabra del handle: VBCOMBO5MACAREPAIR, VBCOMBO10LIPSMOOTH),
        // que solo descuenta ese producto → se cobra exactamente el precio anunciado en la fila (QA Pablo 28-oct; antes
        // VBCOMBO5/10 descontaban todo el pedido)
        const free = cart.items.filter((it) => !pk(it));
        const distinct = (l) => new Set(l.map((it) => it.product_id)).size;
        const word = (it) => up(String(it.handle).split('-')[0]).replace(/[^A-Z0-9]/g, '');
        const allL = free.filter((it) => tag(it) === 'all');
        const done = new Set();
        if (upCodes[1] && distinct(allL) >= 2) allL.forEach((it) => { if (!done.has(it.product_id)) { done.add(it.product_id); want.push(`${up(upCodes[1])}${word(it)}`); } });
        if (upCodes[0] && distinct(cart.items) >= 2) free.filter((it) => tag(it) === 'item').forEach((it) => { if (!done.has(it.product_id)) { done.add(it.product_id); want.push(`${up(upCodes[0])}${word(it)}`); } });
      }
      const w = [...new Set(want.map(up))].slice(0, 5); // Shopify combina 5 códigos como máximo
      const had = (cart.discount_codes || []).map((d) => d.code).filter(isOwn);
      if (had.map(up).sort().join() === [...w].sort().join()) return cart;
      // Un código que Shopify rechaza (aún no creado) no vuelve en discount_codes: no reintentar en bucle el mismo cambio
      const key = `${w.join()}|${cart.items.map((it) => `${it.key}:${it.quantity}`).join()}`;
      if (P.cart.__pkKey === key) return cart;
      P.cart.__pkKey = key;
      try { localStorage.setItem(KEY, JSON.stringify([...new Set([...(Array.isArray(applied) ? applied : []), ...w])].slice(-40))); } catch (e) { /* sin almacenamiento */ }
      return P.cart.discount(w, had.filter((c) => !w.includes(up(c)))).catch(() => cart);
    },
    /* Códigos de descuento del carrito (Ajax API: /cart/update.js con «discount»). Conserva los que
       ya hubiera salvo los de «remove»; con ifEmpty no toca nada si el carrito ya lleva un código. */
    async discount(add = [], remove = [], { ifEmpty = false } = {}) {
      const cur = await fetch(`${routes().cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const up = (c) => String(c || '').trim().toUpperCase();
      const had = (cur.discount_codes || []).map((d) => d.code);
      if (ifEmpty && had.length) return cur;
      const rm = remove.map(up);
      const next = [...new Set([...had.filter((c) => !rm.includes(up(c))), ...add.filter(Boolean)])];
      if (next.map(up).sort().join() === had.map(up).sort().join()) return cur;
      const sections = drawerSections();
      const res = await fetch(`${routes().cart}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: next.join(','), sections, sections_url: location.pathname }),
      });
      let cart;
      try { cart = await readJson(res); } catch (e) { return cur; }
      if (!res.ok) return cur;
      applySections(cart.sections);
      setCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('vb:cart:updated', { detail: cart }));
      return cart;
    },
    async refresh({ render = true } = {}) {
      const cart = await fetch(`${routes().cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      setCount(cart.item_count);
      if (render) {
        const ids = drawerSections();
        if (ids.length) {
          const html = await fetch(`${routes().root.replace(/\/$/, '')}/?sections=${ids.join(',')}`).then((r) => r.json());
          applySections(html);
        }
      }
      document.dispatchEvent(new CustomEvent('vb:cart:updated', { detail: cart }));
      return cart;
    },
    open() {
      const d = document.querySelector('[data-vb-cart-drawer]');
      if (!d) return (location.href = routes().cart);
      // Capas excluyentes: al abrir una, las demás (buscador, mercado, menú) se cierran
      document.dispatchEvent(new CustomEvent('vb:overlay', { detail: 'cart' }));
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      document.querySelector('[data-vb-cart-overlay]')?.classList.add('is-open');
      document.body.classList.add('vb-lock');
      // El cajón pasa de visibility:hidden a visible con la clase: enfocar en el siguiente fotograma
      setTimeout(() => d.querySelector('[data-vb-cart-close]')?.focus({ preventScroll: true }), 60);
    },
    close() {
      const d = document.querySelector('[data-vb-cart-drawer]');
      if (!d) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      document.querySelector('[data-vb-cart-overlay]')?.classList.remove('is-open');
      document.body.classList.remove('vb-lock');
    },
  };

  /* Delegación global del carrito ----------------------------------- */
  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-vb-cart-open]');
    if (openBtn && P.cartType !== 'page' && document.querySelector('[data-vb-cart-drawer]')) {
      e.preventDefault();
      P.cart.refresh().catch(() => {}).then(P.cart.open); // si /cart.js falla (429), se abre igual con lo que hay
      return;
    }
    if (e.target.closest('[data-vb-cart-close]') || e.target.closest('[data-vb-cart-overlay]')) {
      e.preventDefault();
      P.cart.close();
      return;
    }
    const qty = e.target.closest('[data-vb-line-qty]');
    if (qty) {
      e.preventDefault();
      const lineEl = qty.closest('[data-vb-line]');
      lineEl?.classList.add('is-loading');
      lineEl?.querySelector('.vb-cart-line__error')?.remove();
      P.cart.change(Number(qty.dataset.line), Number(qty.dataset.vbLineQty))
        .catch((err) => {
          if (!lineEl) return;
          const msg = document.createElement('p');
          msg.className = 'vb-cart-line__error';
          msg.setAttribute('role', 'alert');
          msg.textContent = err.message;
          (lineEl.querySelector('.vb-cart-line__actions') || lineEl).after(msg);
        })
        .finally(() => lineEl?.classList.remove('is-loading'));
    }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') P.cart.close(); });
  document.addEventListener('vb:overlay', (e) => {
    if (e.detail !== 'cart' && document.querySelector('[data-vb-cart-drawer].is-open')) P.cart.close();
  });

  /* Formularios de producto estándar (data-vb-product-form) -------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-vb-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    btn?.classList.add('is-loading');
    const fd = new FormData(form);
    const properties = {};
    for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v) properties[m[1]] = v; }
    try {
      await P.cart.add([{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1), properties }]);
    } catch (err) {
      const msg = form.querySelector('[data-vb-form-error]');
      if (msg) { msg.textContent = err.message; msg.hidden = false; }
    } finally {
      btn?.classList.remove('is-loading');
    }
  });

  /* Carruseles: flechas, puntos y estado ---------------------------- */
  P.scroller = (root) => {
    if (!root || root.__vbScroller) return;
    root.__vbScroller = true;
    const track = root.querySelector('[data-vb-track]');
    if (!track) return;
    const prev = root.querySelectorAll('[data-vb-prev]');
    const next = root.querySelectorAll('[data-vb-next]');
    const dots = root.querySelector('[data-vb-dots]');
    const items = () => [...track.children].filter((c) => c.offsetParent !== null);
    const step = () => {
      const [a, b] = items();
      return a && b ? b.offsetLeft - a.offsetLeft : track.clientWidth;
    };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      root.classList.toggle('is-scrollable', max > 0);
      prev.forEach((b) => (b.disabled = track.scrollLeft <= 2));
      next.forEach((b) => (b.disabled = track.scrollLeft >= max));
      if (dots) {
        const i = Math.round(track.scrollLeft / (step() || 1));
        [...dots.children].forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
      }
    };
    prev.forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' })));
    next.forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' })));
    if (dots) {
      const build = () => {
        const s = step() || 1;
        const pages = Math.max(1, Math.round((track.scrollWidth - track.clientWidth) / s) + 1);
        dots.innerHTML = '';
        dots.hidden = pages < 2;
        for (let i = 0; i < pages; i++) {
          const d = document.createElement('button');
          d.type = 'button';
          d.setAttribute('aria-label', String(i + 1));
          d.addEventListener('click', () => track.scrollTo({ left: i * s, behavior: 'smooth' }));
          dots.appendChild(d);
        }
        update();
      };
      build();
      new ResizeObserver(build).observe(track);
    }
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    if (track.hasAttribute('data-vb-drag')) drag(track);
    update();
  };

  /* Arrastrar con el ratón (táctil ya es nativo). Durante el gesto se desactiva el snap;
     al soltar se desliza suave hasta la tarjeta más cercana en la dirección del gesto. */
  const drag = (track) => {
    if (track._vbDrag) return; // idempotente: lo llaman init y la capa de motion
    track._vbDrag = true;
    let down = false, moved = false, startX = 0, startL = 0, lastX = 0, lastT = 0, v = 0, pid = null;
    const items = () => [...track.children].filter((c) => c.offsetParent !== null);
    const restore = () => { track.style.scrollSnapType = ''; track.style.scrollBehavior = ''; };
    const settle = () => {
      const pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      const base = track.getBoundingClientRect().left + pad;
      const pos = items().map((c) => track.scrollLeft + c.getBoundingClientRect().left - base);
      if (!pos.length) return restore();
      const max = track.scrollWidth - track.clientWidth;
      const cur = track.scrollLeft;
      const proj = cur - v * 260; // inercia: el destino se proyecta con la velocidad del gesto
      let i = pos.reduce((best, p, n) => (Math.abs(p - proj) < Math.abs(pos[best] - proj) ? n : best), 0);
      if (v < -0.25 && pos[i] < cur) i = Math.min(pos.length - 1, i + 1);
      if (v > 0.25 && pos[i] > cur) i = Math.max(0, i - 1);
      const target = Math.max(0, Math.min(max, pos[i]));
      track.style.scrollBehavior = 'smooth';
      track.scrollTo({ left: target, behavior: 'smooth' });
      let done = false;
      const fin = () => { if (done) return; done = true; restore(); };
      track.addEventListener('scrollend', fin, { once: true });
      setTimeout(fin, 700);
    };
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true; moved = false; pid = e.pointerId;
      startX = lastX = e.clientX; startL = track.scrollLeft; lastT = performance.now(); v = 0;
    });
    track.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        try { track.setPointerCapture(pid); } catch (_) {}
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
        track.classList.add('is-dragging');
      }
      if (!moved) return;
      const now = performance.now();
      v = 0.8 * ((e.clientX - lastX) / Math.max(1, now - lastT)) + 0.2 * v;
      lastX = e.clientX; lastT = now;
      track.scrollLeft = startL - dx;
    });
    const end = () => {
      if (!down) return;
      down = false;
      if (!moved) return;
      track.classList.remove('is-dragging');
      if (performance.now() - lastT > 120) v = 0;
      settle();
      setTimeout(() => { moved = false; }, 0);
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('lostpointercapture', end);
    window.addEventListener('mouseup', end);
    track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
    track.addEventListener('dragstart', (e) => e.preventDefault());
  };
  P.drag = drag;

  /* Aparición al hacer scroll ------------------------------------------ */
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      }), { rootMargin: '0px 0px -8% 0px', threshold: 0.05 })
    : null;

  const init = (scope = document) => {
    scope.querySelectorAll('[data-vb-scroller]').forEach(P.scroller);
    scope.querySelectorAll('.vb-reveal:not(.is-in)').forEach((el) => (io ? io.observe(el) : el.classList.add('is-in')));
  };
  P.init = init;
  init();

  /* Editor de temas: re-inicializar al cambiar secciones ------------- */
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('.vb-reveal').forEach((el) => el.classList.add('is-in'));
    init(e.target);
  });
  if (window.Shopify && Shopify.designMode) document.documentElement.classList.remove('vb-js-reveal');
})();

/* Cualquier cambio del carrito (cajón, ficha, packs, en cualquier página) recalcula el código de pack */
(() => {
  let t;
  document.addEventListener('vb:cart:updated', (e) => {
    const P = window.VB;
    if (!P || !P.cart || !P.cart.syncPacks || !e.detail || !Array.isArray(e.detail.items)) return;
    clearTimeout(t);
    t = setTimeout(() => P.cart.syncPacks(e.detail), 300);
  });
})();

/* Políticas: última palabra del H1 en granate, como el resto de títulos */
(() => {
  document.querySelectorAll('.shopify-policy__title h1').forEach((h) => {
    if (h.querySelector('strong')) return;
    const w = h.textContent.trim().split(/\s+/);
    if (w.length < 2) return;
    const s = document.createElement('strong');
    s.textContent = w.pop();
    h.textContent = w.join(' ') + ' ';
    h.append(s);
  });
})();
